import React, { useState, useContext, useEffect } from "react";
import { IGoal } from "../../../../types/dashboard";
import { AppContext } from "../../../../appContext";
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

import './GoalModal.css';

interface IGoalModalProps {
    goal: IGoal | null;
    onSave: (goalData: Partial<IGoal>) => void;
    onClose: () => void;
}

export const GoalModal: React.FC<IGoalModalProps> = ({ goal, onSave, onClose }) => {
    const { translate } = useContext(AppContext);
    
    const [title, setTitle] = useState(goal?.title || '');
    const [emoji, setEmoji] = useState(goal?.emoji || '🎯');
    const [startDate, setStartDate] = useState(
        goal?.startDate ? new Date(goal.startDate).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0]
    );
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        setEmoji(emojiData.emoji);
        setShowEmojiPicker(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!title.trim()) {
            alert(translate('goals_title_required') || 'Please enter a goal title');
            return;
        }

        onSave({
            title: title.trim(),
            emoji,
            startDate: new Date(startDate).toISOString()
        });
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="goal_modal_backdrop" onClick={handleBackdropClick}>
            <div className="goal_modal">
                <div className="goal_modal_header">
                    <h2 className="goal_modal_title">
                        {goal ? translate('goals_edit') || 'Edit Goal' : translate('goals_add') || 'Add Goal'}
                    </h2>
                    <button className="goal_modal_close" onClick={onClose}>×</button>
                </div>

                <form className="goal_modal_form" onSubmit={handleSubmit}>
                    <div className="goal_modal_field">
                        <label className="goal_modal_label">
                            {translate('goals_field_emoji') || 'Emoji'}
                        </label>
                        <div className="goal_modal_emoji_input">
                            <button
                                type="button"
                                className="goal_modal_emoji_button"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            >
                                <span className="goal_modal_emoji_preview">{emoji}</span>
                                <span className="goal_modal_emoji_text">
                                    {translate('goals_select_emoji') || 'Select emoji'}
                                </span>
                            </button>
                            {showEmojiPicker && (
                                <div className="goal_modal_emoji_picker">
                                    <EmojiPicker
                                        onEmojiClick={handleEmojiClick}
                                        width={300}
                                        height={400}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="goal_modal_field">
                        <label className="goal_modal_label">
                            {translate('goals_field_title') || 'Goal Title'}
                        </label>
                        <input
                            type="text"
                            className="goal_modal_input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={translate('goals_title_placeholder') || 'e.g., days without smoking'}
                            maxLength={50}
                        />
                    </div>

                    <div className="goal_modal_field">
                        <label className="goal_modal_label">
                            {translate('goals_field_start_date') || 'Start Date'}
                        </label>
                        <input
                            type="date"
                            className="goal_modal_input"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>

                    <div className="goal_modal_actions">
                        <button type="button" className="goal_modal_cancel" onClick={onClose}>
                            {translate('common_cancel') || 'Cancel'}
                        </button>
                        <button type="submit" className="goal_modal_save">
                            {translate('common_save') || 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
